import { EmailVerificationBanner } from '@components/ui/EmailVerificationBanner';
import { PastDueBanner } from '@components/ui/PastDueBanner';
import { WalletBanner } from '@components/wallet/WalletBanner';
import { WalletBlockerOverlay } from '@components/wallet/WalletBlockerOverlay';
import { useInboundMessageSound } from '@hooks/useInboundMessageSound';
import { useInboxRealtime } from '@hooks/useInboxRealtime';
import { useWalletStatus } from '@hooks/useWalletStatus';
import { cn } from '@lib/utils';
import { useUiStore } from '@stores/ui.store';
import { Outlet } from 'react-router-dom';



import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

/**
 * Master layout wrapping every authenticated page. Sidebar (fixed left) +
 * TopBar (sticky top) + scrollable content area. ToastContainer is mounted
 * at the App.tsx root so it sits above everything.
 *
 * Realtime hooks (`useInboxRealtime`, `useInboundMessageSound`) mount here
 * so the unread badge bumps and the new-message ding fires on every page,
 * not just the inbox.
 *
 * The wallet gate (`useWalletStatus`) also lives here so it spans the whole
 * workspace: a non-blocking `WalletBanner` for low/warning states, and a
 * full-screen `WalletBlockerOverlay` that locks the app when the wallet is
 * empty or a project is paused.
 */
export function AppLayout(): JSX.Element {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  useInboxRealtime();
  useInboundMessageSound();
  const { status: walletStatus } = useWalletStatus();

  return (
    <div className="flex h-screen overflow-hidden bg-apple-canvas">
      <Sidebar />

      <div
        className={cn(
          'flex flex-1 flex-col min-w-0 transition-[margin-left] duration-250 ease-apple',
        )}
      >
        <TopBar />
        <EmailVerificationBanner />
        <PastDueBanner />
        {walletStatus && <WalletBanner status={walletStatus} />}

        <main className="flex-1 overflow-y-auto">
          <div
            className={cn(
              'mx-auto px-8 py-8',
              collapsed ? 'max-w-content' : 'max-w-content',
            )}
          >
            <Outlet />
          </div>
        </main>
      </div>

      {walletStatus?.state === 'blocked' && (
        <WalletBlockerOverlay status={walletStatus} />
      )}
    </div>
  );
}
